import { MemoryFileSystem } from '@playcanvas/splat-transform';
import { Color, Mat4, path, Quat, Texture, Vec3, Vec4 } from 'playcanvas';

import { EditHistory } from './edit-history';
import { SelectAllOp, SelectNoneOp, SelectInvertOp, SelectOp, HideSelectionOp, UnhideAllOp, DeleteSelectionOp, ResetOp, MultiOp, AddSplatOp, MeshRenameOp } from './edit-ops';
import { Element, ElementType } from './element';
import { Events } from './events';
import { defaultGlobalClipBox } from './global-clip-box';
import { MappedReadFileSystem } from './io';
import { Scene } from './scene';
import { Splat } from './splat';
import { serializePly } from './splat-serialize';
import { UnitMode, UnitSystem } from './unit-system';
import { WalkPresetManager } from './walk-presets';

const removeExtension = (filename: string) => {
    return filename.substring(0, filename.length - path.getExtension(filename).length);
};

type CaseMetadata = {
    caseType?: 'accident' | 'premises' | 'crime-scene' | 'construction' | 'general';
    caseNumber?: string;
    caseName?: string;
    jurisdiction?: string;
    incidentDate?: string;
    scanDate?: string;
    preparedBy?: string;
    disclaimers?: string[];
};

const cloneCaseMetadata = (value: CaseMetadata | undefined) => {
    if (!value) {
        return undefined;
    }
    return {
        ...value,
        disclaimers: value.disclaimers?.slice()
    };
};

const mergeCaseMetadata = (current: CaseMetadata | undefined, patch: Partial<CaseMetadata>) => {
    const merged: CaseMetadata = {
        ...(current ?? {}),
        ...patch
    };

    if (Object.prototype.hasOwnProperty.call(patch, 'disclaimers')) {
        merged.disclaimers = patch.disclaimers?.slice();
    } else if (current?.disclaimers) {
        merged.disclaimers = current.disclaimers.slice();
    }

    const hasValue = Object.values(merged).some(value => value !== undefined);
    return hasValue ? merged : undefined;
};

// register for editor and scene events
const registerEditorEvents = (events: Events, editHistory: EditHistory, scene: Scene) => {
    const vec = new Vec3();
    const vec2 = new Vec3();
    const vec4 = new Vec4();
    const mat = new Mat4();
    const SH_C0 = 0.28209479177387814;
    let caseMetadata: CaseMetadata | undefined;

    // units
    const configuredUnitMode = (scene.config as any)?.units?.mode;
    const unitMode: UnitMode = (configuredUnitMode === 'metric' || configuredUnitMode === 'imperial') ? configuredUnitMode : 'imperial';
    const unitSystem = new UnitSystem(unitMode);

    const decodeColorChannel = (value: number) => {
        return Math.min(1, Math.max(0, 0.5 + value * SH_C0));
    };

    // get the list of selected splats (currently limited to just a single one)
    const selectedSplats = () => {
        const selected = events.invoke('selection') as Splat;
        return selected?.visible ? [selected] : [];
    };

    let lastExportCursor = 0;

    // add unsaved changes warning message.
    window.addEventListener('beforeunload', (e) => {
        if (!events.invoke('scene.dirty')) {
            // if the undo cursor matches last export, then we have no unsaved changes
            return undefined;
        }

        const msg = 'You have unsaved changes. Are you sure you want to leave?';
        e.returnValue = msg;
        return msg;
    });

    events.function('targetSize', () => {
        return scene.targetSize;
    });

    events.on('scene.clear', () => {
        scene.clear();
        editHistory.clear();
        lastExportCursor = 0;
    });

    // When a splat is removed from the scene, remove all edit operations that reference it
    events.on('scene.elementRemoved', (element: Element) => {
        if (element.type === ElementType.splat) {
            editHistory.removeForSplat(element as Splat);
        }
    });

    events.function('scene.dirty', () => {
        return editHistory.cursor !== lastExportCursor;
    });

    events.on('doc.saved', () => {
        lastExportCursor = editHistory.cursor;
    });

    events.function('case.metadata', () => {
        return cloneCaseMetadata(caseMetadata);
    });

    events.on('case.setMetadata', (patch: Partial<CaseMetadata>) => {
        caseMetadata = mergeCaseMetadata(caseMetadata, patch ?? {});
        events.fire('case.metadataChanged', cloneCaseMetadata(caseMetadata));
    });

    // units (scene-global)

    events.function('units.mode', () => {
        return unitSystem.mode;
    });

    events.function('units.system', () => {
        return unitSystem;
    });

    events.on('units.setMode', (mode: UnitMode) => {
        if (mode !== 'metric' && mode !== 'imperial') {
            return;
        }

        if (mode !== unitSystem.mode) {
            unitSystem.mode = mode;
            events.fire('units.mode', unitSystem.mode);
            events.fire('units.changed', unitSystem.mode);
        }
    });

    // Mesh rename requests (from UI) -> undoable edit op
    // NOTE: Mesh itself also fires 'mesh.name' when its name changes (no newName arg).
    events.on('mesh.name', (mesh: any, newName?: unknown) => {
        if (typeof newName !== 'string') {
            return;
        }

        const trimmed = newName.trim();
        if (!trimmed) {
            return;
        }

        if (mesh && typeof mesh.name === 'string' && trimmed !== mesh.name) {
            events.fire('edit.add', new MeshRenameOp(mesh, trimmed));
        }
    });

    // force render on some events

    [
        'camera.mode', 'camera.overlay', 'camera.splatSize', 'view.outlineSelection',
        'view.centersUseGaussianColor', 'view.bands', 'camera.bound', 'camera.showPoses', 'bookmarks.showMarkers',
        'selection.changed', 'tool.coordSpace', 'clipBox.changed'
    ].forEach((eventName) => {
        events.on(eventName, () => {
            scene.forceRender = true;
        });
    });

    // grid.visible

    const setGridVisible = (visible: boolean) => {
        if (visible !== scene.grid.visible) {
            scene.grid.visible = visible;
            events.fire('grid.visible', visible);
        }
    };

    events.function('grid.visible', () => {
        return scene.grid.visible;
    });

    events.on('grid.setVisible', (visible: boolean) => {
        setGridVisible(visible);
    });

    events.on('grid.toggleVisible', () => {
        setGridVisible(!scene.grid.visible);
    });

    setGridVisible(scene.config.show.grid);

    // clipBox

    let clipBox = defaultGlobalClipBox();
    let clipBoxMode: 'shader' | 'test' | 'occluder' = 'shader';

    events.function('clipBox', () => {
        return clipBox;
    });

    events.on('clipBox.set', (partial: { enabled?: boolean; center?: Vec3; halfExtents?: Vec3; rotation?: any }) => {
        clipBox = {
            ...clipBox,
            ...partial,
            center: partial?.center ?? clipBox.center,
            halfExtents: partial?.halfExtents ?? clipBox.halfExtents,
            rotation: partial?.rotation ?? clipBox.rotation
        };
        events.fire('clipBox.changed', clipBox);
    });

    events.function('clipBox.mode', () => {
        return clipBoxMode;
    });

    events.on('clipBox.setMode', (mode: 'shader' | 'test' | 'occluder') => {
        if (mode !== 'shader' && mode !== 'test' && mode !== 'occluder') {
            return;
        }
        if (mode !== clipBoxMode) {
            clipBoxMode = mode;
            events.fire('clipBox.modeChanged', clipBoxMode);
        }
    });

    // camera.fov

    const setCameraFov = (fov: number) => {
        if (fov !== scene.camera.fov) {
            scene.camera.fov = fov;
            events.fire('camera.fov', scene.camera.fov);
        }
    };

    events.function('camera.fov', () => {
        return scene.camera.fov;
    });

    events.on('camera.setFov', (fov: number) => {
        setCameraFov(fov);
    });

    // camera pick debug

    let cameraPickDebug = false;
    let cameraLastPickDebug: any = null;

    events.function('camera.pickDebug', () => {
        return cameraPickDebug;
    });

    events.function('camera.lastPickDebug', () => {
        return cameraLastPickDebug;
    });

    events.on('camera.setPickDebug', (enabled: boolean) => {
        cameraPickDebug = !!enabled;
        if (!cameraPickDebug) {
            cameraLastPickDebug = null;
        }
    });

    events.on('camera.setLastPickDebug', (payload: any) => {
        cameraLastPickDebug = payload ?? null;
    });

    // camera.tonemapping

    events.function('camera.tonemapping', () => {
        return scene.camera.tonemapping;
    });

    events.on('camera.setTonemapping', (value: string) => {
        scene.camera.tonemapping = value;
    });

    // camera.bound

    let bound = scene.config.show.bound;

    const setBoundVisible = (visible: boolean) => {
        const next = !!visible;

        // Keep both config paths synchronized.
        scene.config.show.bound = next;
        scene.config.debug.showBound = next;

        if (next !== bound) {
            bound = next;
            events.fire('camera.bound', bound);
        }
    };

    events.function('camera.bound', () => {
        return bound;
    });

    events.on('camera.setBound', (value: boolean) => {
        setBoundVisible(value);
    });

    events.on('camera.toggleBound', () => {
        setBoundVisible(!events.invoke('camera.bound'));
    });

    // Initialize and synchronize bound visibility state.
    setBoundVisible(bound);

    // camera.showPoses

    let showPoses = scene.config.show.cameraPoses;

    const setShowPoses = (visible: boolean) => {
        if (visible !== showPoses) {
            showPoses = visible;
            events.fire('camera.showPoses', showPoses);
        }
    };

    events.function('camera.showPoses', () => {
        return showPoses;
    });

    events.on('camera.setShowPoses', (value: boolean) => {
        setShowPoses(value);
    });

    events.on('camera.toggleShowPoses', () => {
        setShowPoses(!events.invoke('camera.showPoses'));
    });

    // bookmarks.showMarkers

    let showBookmarkMarkers = scene.config.show.bookmarkMarkers;

    const setShowBookmarkMarkers = (visible: boolean) => {
        if (visible !== showBookmarkMarkers) {
            showBookmarkMarkers = visible;
            events.fire('bookmarks.showMarkers', showBookmarkMarkers);
        }
    };

    events.function('bookmarks.showMarkers', () => {
        return showBookmarkMarkers;
    });

    events.on('bookmarks.setShowMarkers', (value: boolean) => {
        setShowBookmarkMarkers(value);
    });

    events.on('bookmarks.toggleShowMarkers', () => {
        setShowBookmarkMarkers(!events.invoke('bookmarks.showMarkers'));
    });

    // camera.focus

    events.on('camera.focus', () => {
        const splat = selectedSplats()[0];
        if (splat) {
            // use current bounds (caller should have awaited the operation that changed data)
            const bound = splat.numSelected > 0 ?
                splat.selectionBound :
                splat.localBound;
            vec.copy(bound.center);

            const worldTransform = splat.worldTransform;
            worldTransform.transformPoint(vec, vec);
            worldTransform.getScale(vec2);

            scene.camera.focus({
                focalPoint: vec,
                radius: bound.halfExtents.length() * vec2.x,
                speed: 1
            });
        }
    });

    events.on('camera.centerOn', () => {
        events.fire('camera.focus');
    });

    events.on('measurements.focus', (idOrNumber: string | number | null | undefined) => {
        const items = events.invoke('measurements.list') as Array<{ id?: string; number?: number; points?: Array<[number, number, number]> }> | undefined;
        if (!Array.isArray(items) || items.length === 0) {
            return;
        }

        const selected = items.find((item) => {
            if (typeof idOrNumber === 'string') {
                return item?.id === idOrNumber;
            }
            if (typeof idOrNumber === 'number' && Number.isFinite(idOrNumber)) {
                return item?.number === idOrNumber;
            }
            return false;
        });

        const points = selected?.points;
        if (!Array.isArray(points) || points.length === 0) {
            return;
        }

        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let minZ = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;
        let maxZ = Number.NEGATIVE_INFINITY;

        for (const point of points) {
            if (!Array.isArray(point) || point.length !== 3) {
                continue;
            }
            const x = Number(point[0]);
            const y = Number(point[1]);
            const z = Number(point[2]);
            if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
                continue;
            }

            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            minZ = Math.min(minZ, z);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
            maxZ = Math.max(maxZ, z);
        }

        if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(minZ) || !Number.isFinite(maxX) || !Number.isFinite(maxY) || !Number.isFinite(maxZ)) {
            return;
        }

        const center = new Vec3((minX + maxX) * 0.5, (minY + maxY) * 0.5, (minZ + maxZ) * 0.5);
        const dx = maxX - minX;
        const dy = maxY - minY;
        const dz = maxZ - minZ;
        const radius = Math.max(0.25, Math.sqrt(dx * dx + dy * dy + dz * dz) * 0.5);

        scene.camera.focus({
            focalPoint: center,
            radius,
            speed: 1
        });
    });

    events.on('camera.reset', () => {
        const { initialAzim, initialElev, initialZoom } = scene.config.controls;
        const x = Math.sin(initialAzim * Math.PI / 180) * Math.cos(initialElev * Math.PI / 180);
        const y = -Math.sin(initialElev * Math.PI / 180);
        const z = Math.cos(initialAzim * Math.PI / 180) * Math.cos(initialElev * Math.PI / 180);
        const zoom = initialZoom;

        scene.camera.setPose(new Vec3(x * zoom, y * zoom, z * zoom), new Vec3(0, 0, 0));
    });

    // handle camera align events
    events.on('camera.align', (axis: string) => {
        switch (axis) {
            case 'px': scene.camera.setAzimElev(90, 0); break;
            case 'py': scene.camera.setAzimElev(0, -90); break;
            case 'pz': scene.camera.setAzimElev(0, 0); break;
            case 'nx': scene.camera.setAzimElev(270, 0); break;
            case 'ny': scene.camera.setAzimElev(0, 90); break;
            case 'nz': scene.camera.setAzimElev(180, 0); break;
        }

        // switch to ortho mode
        scene.camera.ortho = true;
    });

    // returns true if the selected splat has selected gaussians
    events.function('selection.splats', () => {
        const splat = events.invoke('selection') as Splat;
        return splat?.numSelected > 0;
    });

    events.on('select.all', () => {
        selectedSplats().forEach((splat) => {
            events.fire('edit.add', new SelectAllOp(splat));
        });
    });

    events.on('select.none', () => {
        selectedSplats().forEach((splat) => {
            events.fire('edit.add', new SelectNoneOp(splat));
        });
    });

    events.on('select.invert', () => {
        selectedSplats().forEach((splat) => {
            events.fire('edit.add', new SelectInvertOp(splat));
        });
    });

    events.on('select.pred', (op, pred: (i: number) => boolean) => {
        selectedSplats().forEach((splat) => {
            events.fire('edit.add', new SelectOp(splat, op, pred));
        });
    });

    const intersectCenters = async (splat: Splat, op: 'add'|'remove'|'set', options: any) => {
        const data = await scene.dataProcessor.intersect(options, splat);
        const filter = (i: number) => data[i] === 255;
        events.fire('edit.add', new SelectOp(splat, op, filter));
    };

    events.on('select.bySphere', async (op: 'add'|'remove'|'set', sphere: number[]) => {
        for (const splat of selectedSplats()) {
            await intersectCenters(splat, op, {
                sphere: { x: sphere[0], y: sphere[1], z: sphere[2], radius: sphere[3] }
            });
        }
    });

    events.on('select.byBox', async (op: 'add'|'remove'|'set', box: number[]) => {
        for (const splat of selectedSplats()) {
            await intersectCenters(splat, op, {
                box: { x: box[0], y: box[1], z: box[2], lenx: box[3], leny: box[4], lenz: box[5] }
            });
        }
    });

    events.function('select.rect', async (op: 'add'|'remove'|'set', rect: any) => {
        const mode = events.invoke('camera.mode');

        for (const splat of selectedSplats()) {
            if (mode === 'centers') {
                await intersectCenters(splat, op, {
                    rect: { x1: rect.start.x, y1: rect.start.y, x2: rect.end.x, y2: rect.end.y }
                });
            } else if (mode === 'rings') {
                scene.camera.pickPrep(splat, op);
                const pick = await scene.camera.pickRect(
                    rect.start.x,
                    rect.start.y,
                    rect.end.x - rect.start.x,
                    rect.end.y - rect.start.y
                );

                const sortedIds = new Uint32Array(new Set(pick)).sort();
                events.fire('edit.add', new SelectOp(splat, op, sortedIds));
            }
        }
    });

    let maskTexture: Texture = null;

    events.function('select.byMask', async (op: 'add'|'remove'|'set', canvas: HTMLCanvasElement, context: CanvasRenderingContext2D) => {
        const mode = events.invoke('camera.mode');

        for (const splat of selectedSplats()) {
            if (mode === 'centers') {
                // create mask texture
                if (!maskTexture || maskTexture.width !== canvas.width || maskTexture.height !== canvas.height) {
                    if (maskTexture) {
                        maskTexture.destroy();
                    }
                    maskTexture = new Texture(scene.graphicsDevice);
                }
                maskTexture.setSource(canvas);

                await intersectCenters(splat, op, {
                    mask: maskTexture
                });
            } else if (mode === 'rings') {
                const mask = context.getImageData(0, 0, canvas.width, canvas.height);

                // calculate mask bound so we limit pixel operations
                let mx0 = mask.width - 1;
                let my0 = mask.height - 1;
                let mx1 = 0;
                let my1 = 0;
                for (let y = 0; y < mask.height; ++y) {
                    for (let x = 0; x < mask.width; ++x) {
                        if (mask.data[(y * mask.width + x) * 4 + 3] === 255) {
                            mx0 = Math.min(mx0, x);
                            my0 = Math.min(my0, y);
                            mx1 = Math.max(mx1, x);
                            my1 = Math.max(my1, y);
                        }
                    }
                }

                // Convert mask bounds to normalized coordinates
                const nx0 = mx0 / mask.width;
                const ny0 = my0 / mask.height;
                const nx1 = (mx1 + 1) / mask.width;
                const ny1 = (my1 + 1) / mask.height;
                const nw = nx1 - nx0;
                const nh = ny1 - ny0;

                scene.camera.pickPrep(splat, op);
                const pick = await scene.camera.pickRect(nx0, ny0, nw, nh);

                // Calculate actual pixel dimensions for iteration
                const { width, height } = scene.targetSize;

                // Convert normalized coordinates to render target pixels
                const px = Math.floor(nx0 * width);
                const py = Math.floor(ny0 * height);
                const pw = Math.max(1, Math.ceil((nx0 + nw) * width) - px);
                const ph = Math.max(1, Math.ceil((ny0 + nh) * height) - py);

                const selected = new Set<number>();
                for (let y = 0; y < ph; ++y) {
                    for (let x = 0; x < pw; ++x) {
                        const mx = Math.floor((nx0 + x / width) * mask.width);
                        const my = Math.floor((ny0 + y / height) * mask.height);
                        if (mask.data[(my * mask.width + mx) * 4] === 255) {
                            selected.add(pick[(ph - 1 - y) * pw + x]);
                        }
                    }
                }

                const sortedIds = new Uint32Array(selected).sort();
                events.fire('edit.add', new SelectOp(splat, op, sortedIds));
            }
        }
    });

    events.function('select.point', async (op: 'add'|'remove'|'set', point: { x: number, y: number }) => {
        const { width, height } = scene.targetSize;
        const mode = events.invoke('camera.mode');

        for (const splat of selectedSplats()) {
            const splatData = splat.splatData;

            if (mode === 'centers') {
                const x = splatData.getProp('x');
                const y = splatData.getProp('y');
                const z = splatData.getProp('z');

                const splatSize = events.invoke('camera.splatSize');
                const camera = scene.camera.camera;
                const sx = point.x * width;
                const sy = point.y * height;

                // calculate final matrix
                mat.mul2(camera.camera._viewProjMat, splat.worldTransform);

                const filter = (i: number) => {
                    vec4.set(x[i], y[i], z[i], 1.0);
                    mat.transformVec4(vec4, vec4);
                    const px = (vec4.x / vec4.w * 0.5 + 0.5) * width;
                    const py = (-vec4.y / vec4.w * 0.5 + 0.5) * height;
                    return Math.abs(px - sx) < splatSize && Math.abs(py - sy) < splatSize;
                };

                events.fire('edit.add', new SelectOp(splat, op, filter));
            } else if (mode === 'rings') {
                scene.camera.pickPrep(splat, op);

                // Use normalized coordinates with minimal size for single pixel pick
                const pickResult = await scene.camera.pickRect(
                    point.x,
                    point.y,
                    1 / width,
                    1 / height
                );
                const pickId = pickResult[0];
                events.fire('edit.add', new SelectOp(splat, op, new Uint32Array([pickId])));
            }
        }
    });

    // Eyedropper selection with SelectOp so undo/redo and selection state updates remain consistent.
    // Threshold acts as a per-channel absolute difference: 0 only matches identical colors while 1 matches everything.
    // TO DO:
    // -  alternative distance metrics such as HSV.
    // -  alternative UI for threshold, two handles for min/max?
    events.function('select.colorMatch', async (op: 'add'|'remove'|'set', point: { x: number, y: number }, threshold = 0) => {
        const splats = selectedSplats();
        const targetSize = scene.targetSize;
        if (!splats.length || !targetSize || !point) {
            return;
        }

        const { width, height } = targetSize;
        if (!width || !height) {
            return;
        }

        // Clamp normalized coordinates to valid range
        const nx = Math.max(0, Math.min(1, point.x));
        const ny = Math.max(0, Math.min(1, point.y));
        const colorThreshold = Math.min(1, Math.max(0, Number.isFinite(threshold) ? threshold : 0));

        for (const splat of splats) {
            scene.camera.pickPrep(splat, 'set');
            // Use normalized coordinates with minimal size for single pixel pick
            const pickBuffer = await scene.camera.pickRect(nx, ny, 1 / width, 1 / height);
            const pickId = pickBuffer?.[0];
            if (pickId === undefined || pickId === 0xffffffff) {
                continue;
            }

            const reds = splat.splatData.getProp('f_dc_0') as Float32Array;
            const greens = splat.splatData.getProp('f_dc_1') as Float32Array;
            const blues = splat.splatData.getProp('f_dc_2') as Float32Array;
            // validate pickId and color channels exist
            if (!reds || !greens || !blues || pickId < 0 || pickId >= reds.length) {
                continue;
            }
            // decode color channels for the reference pixel
            const reference = [
                decodeColorChannel(reds[pickId]),
                decodeColorChannel(greens[pickId]),
                decodeColorChannel(blues[pickId])
            ];
            // Check if a value is within the color threshold of the reference
            const withinThreshold = (value: number, ref: number) => Math.abs(value - ref) <= colorThreshold;

            // filter to select pixels within the color threshold
            const filter = (i: number) => {
                return withinThreshold(decodeColorChannel(reds[i]), reference[0]) &&
                    withinThreshold(decodeColorChannel(greens[i]), reference[1]) &&
                    withinThreshold(decodeColorChannel(blues[i]), reference[2]);
            };

            events.fire('edit.add', new SelectOp(splat, op, filter));
        }
    });

    events.on('select.hide', () => {
        selectedSplats().forEach((splat) => {
            events.fire('edit.add', new HideSelectionOp(splat));
        });
    });

    events.on('select.unhide', () => {
        selectedSplats().forEach((splat) => {
            events.fire('edit.add', new UnhideAllOp(splat));
        });
    });

    events.on('select.delete', () => {
        // Don't delete gaussians when measure tool is active (backspace deletes measure points instead)
        if (events.invoke('tool.active') === 'measure') {
            return;
        }
        selectedSplats().forEach((splat) => {
            editHistory.add(new DeleteSelectionOp(splat));
        });
    });

    const performSelectionFunc = async (func: 'duplicate' | 'separate') => {
        const splats = selectedSplats();

        const memFs = new MemoryFileSystem();

        await serializePly(splats, {
            maxSHBands: 3,
            selected: true
        }, memFs);

        const data = memFs.results.get('output.ply');

        if (data) {
            const splat = splats[0];

            // wrap PLY in a blob and load it
            const blob = new Blob([data.buffer as ArrayBuffer], { type: 'application/octet-stream' });
            const filename = `${removeExtension(splat.filename)}.ply`;
            const fileSystem = new MappedReadFileSystem();
            fileSystem.addFile(filename, blob);
            const copy = await scene.assetLoader.load(filename, fileSystem);
            if (!(copy instanceof Splat)) {
                return;
            }

            if (func === 'separate') {
                editHistory.add(new MultiOp([
                    new DeleteSelectionOp(splat),
                    new AddSplatOp(scene, copy)
                ]));
            } else {
                editHistory.add(new AddSplatOp(scene, copy));
            }
        }
    };

    // duplicate the current selection
    events.on('select.duplicate', async () => {
        await performSelectionFunc('duplicate');
    });

    events.on('select.separate', async () => {
        await performSelectionFunc('separate');
    });

    events.on('scene.reset', () => {
        selectedSplats().forEach((splat) => {
            editHistory.add(new ResetOp(splat));
        });
    });

    // camera mode (visual: centers/rings)

    let activeMode = 'centers';

    const setCameraMode = (mode: string) => {
        if (mode !== activeMode) {
            activeMode = mode;
            events.fire('camera.mode', activeMode);
        }
    };

    events.function('camera.mode', () => {
        return activeMode;
    });

    events.on('camera.setMode', (mode: string) => {
        setCameraMode(mode);
    });

    events.on('camera.toggleMode', () => {
        setCameraMode(events.invoke('camera.mode') === 'centers' ? 'rings' : 'centers');
    });

    // picture mode gating

    let pictureEnabled = false;

    events.function('picture.enabled', () => {
        return pictureEnabled && !events.invoke('camera.posesNeedsValidation');
    });

    events.on('picture.setEnabled', (enabled: boolean) => {
        const next = !!enabled && !events.invoke('camera.posesNeedsValidation');
        if (next !== pictureEnabled) {
            pictureEnabled = next;
            events.fire('picture.enabledChanged', pictureEnabled);
        }
    });

    // camera control mode (orbit/fly)

    let controlMode: 'orbit' | 'fly' | 'walk' = 'orbit';

    const setControlMode = (mode: 'orbit' | 'fly' | 'walk') => {
        if (mode !== controlMode) {
            controlMode = mode;
            scene.camera.controlMode = mode;
            events.fire('camera.controlMode', controlMode);
        }
    };

    events.function('camera.controlMode', () => {
        return controlMode;
    });

    events.on('camera.setControlMode', (mode: 'orbit' | 'fly' | 'walk') => {
        setControlMode(mode);
    });

    events.on('camera.toggleControlMode', () => {
        setControlMode(controlMode === 'orbit' ? 'fly' : 'orbit');
    });

    // camera overlay

    let cameraOverlay = scene.config.camera.overlay;

    const setCameraOverlay = (enabled: boolean) => {
        if (enabled !== cameraOverlay) {
            cameraOverlay = enabled;
            events.fire('camera.overlay', cameraOverlay);
        }
    };

    events.function('camera.overlay', () => {
        return cameraOverlay;
    });

    events.on('camera.setOverlay', (value: boolean) => {
        setCameraOverlay(value);
    });

    events.on('camera.toggleOverlay', () => {
        setCameraOverlay(!events.invoke('camera.overlay'));
    });

    // splat size

    let splatSize = 2;

    const setSplatSize = (value: number) => {
        if (value !== splatSize) {
            splatSize = value;
            events.fire('camera.splatSize', splatSize);
        }
    };

    events.function('camera.splatSize', () => {
        return splatSize;
    });

    events.on('camera.setSplatSize', (value: number) => {
        setSplatSize(value);
    });

    // camera fly speed

    const setFlySpeed = (value: number) => {
        if (value !== scene.camera.flySpeed) {
            scene.camera.flySpeed = value;
            events.fire('camera.flySpeed', value);
        }
    };

    events.function('camera.flySpeed', () => {
        return scene.camera.flySpeed;
    });

    events.on('camera.setFlySpeed', (value: number) => {
        setFlySpeed(value);
    });

    // camera collision settings

    let collisionSources = 'none';
    let collisionDistance = 0.6;
    let collisionMinSplatOpacity = 0.05;

    events.function('camera.collisionSources', () => collisionSources);
    events.function('camera.collisionDistance', () => collisionDistance);
    events.function('camera.collisionMinSplatOpacity', () => collisionMinSplatOpacity);

    events.on('camera.setCollisionSources', (value: string) => {
        collisionSources = value;
        events.fire('camera.collisionSources', collisionSources);
    });

    events.on('camera.setCollisionDistance', (value: number) => {
        collisionDistance = value;
        events.fire('camera.collisionDistance', collisionDistance);
    });

    events.on('camera.setCollisionMinSplatOpacity', (value: number) => {
        collisionMinSplatOpacity = value;
        events.fire('camera.collisionMinSplatOpacity', collisionMinSplatOpacity);
    });

    // camera walk settings (legacy compatibility)

    // Walk presets (C.9)
    const walkPresetManager = new WalkPresetManager();

    events.function('walk.presets', () => {
        return walkPresetManager.getAll();
    });

    events.function('walk.presets.serialize', () => {
        return walkPresetManager.serialize();
    });

    events.on('walk.presets.deserialize', (data: unknown) => {
        walkPresetManager.deserialize(data);
        events.fire('walk.presets', walkPresetManager.getAll());
    });

    events.function('walk.addPreset', (name: string, heightMeters: number) => {
        const preset = walkPresetManager.addCustomPreset(name, heightMeters);
        events.fire('walk.presets', walkPresetManager.getAll());
        return preset;
    });

    events.function('walk.removePreset', (id: string) => {
        walkPresetManager.removeCustomPreset(id);
        events.fire('walk.presets', walkPresetManager.getAll());
    });

    // Notify UI of the initial preset set (UI is created before editor events are registered).
    events.fire('walk.presets', walkPresetManager.getAll());

    const setWalkSpeed = (value: number) => {
        if (value !== scene.camera.walkSpeed) {
            scene.camera.walkSpeed = value;
            events.fire('camera.walkSpeed', value);
        }
    };

    events.function('camera.walkSpeed', () => {
        return scene.camera.walkSpeed;
    });

    events.on('camera.setWalkSpeed', (value: number) => {
        setWalkSpeed(value);
    });

    const setWalkEnabled = (enabled: boolean) => {
        const value = !!enabled;
        if (value !== scene.camera.walkEnabled) {
            scene.camera.walkEnabled = value;
            events.fire('camera.walkEnabled', value);
        }
    };

    events.function('camera.walkEnabled', () => {
        return scene.camera.walkEnabled;
    });

    events.on('camera.setWalkEnabled', (enabled: boolean) => {
        setWalkEnabled(enabled);
    });

    const setWalkHeight = (height: number) => {
        if (height !== scene.camera.walkHeight) {
            scene.camera.walkHeight = height;
            events.fire('camera.walkHeight', height);
        }
    };

    events.function('camera.walkHeight', () => {
        return scene.camera.walkHeight;
    });

    events.on('camera.setWalkHeight', (height: number) => {
        setWalkHeight(height);
    });

    const setWalkSnapToMesh = (enabled: boolean) => {
        const value = !!enabled;
        if (value !== scene.camera.walkSnapToMesh) {
            scene.camera.walkSnapToMesh = value;
            events.fire('camera.walkSnapToMesh', value);
        }
    };

    events.function('camera.walkSnapToMesh', () => {
        return scene.camera.walkSnapToMesh;
    });

    events.on('camera.setWalkSnapToMesh', (enabled: boolean) => {
        setWalkSnapToMesh(enabled);
    });

    // camera bounds: constrain camera position to a box
    let cameraBoundsEnabled = false;
    let cameraBoundsPadding = 1.5;

    events.function('camera.boundsEnabled', () => cameraBoundsEnabled);
    events.function('camera.boundsPadding', () => cameraBoundsPadding);

    events.on('camera.setBoundsEnabled', (value: boolean) => {
        const v = !!value;
        if (v !== cameraBoundsEnabled) {
            cameraBoundsEnabled = v;
            events.fire('camera.boundsEnabled', cameraBoundsEnabled);
        }
    });

    events.on('camera.setBoundsPadding', (value: number) => {
        const v = Math.max(1, Math.min(10, value));
        if (v !== cameraBoundsPadding) {
            cameraBoundsPadding = v;
            events.fire('camera.boundsPadding', cameraBoundsPadding);
        }
    });

    // outline selection

    let outlineSelection = false;

    const setOutlineSelection = (value: boolean) => {
        if (value !== outlineSelection) {
            outlineSelection = value;
            events.fire('view.outlineSelection', outlineSelection);
        }
    };

    events.function('view.outlineSelection', () => {
        return outlineSelection;
    });

    events.on('view.setOutlineSelection', (value: boolean) => {
        setOutlineSelection(value);
    });

    // view spherical harmonic bands

    let viewBands = scene.config.show.shBands;

    const setViewBands = (value: number) => {
        if (value !== viewBands) {
            viewBands = value;
            events.fire('view.bands', viewBands);
        }
    };

    events.function('view.bands', () => {
        return viewBands;
    });

    events.on('view.setBands', (value: number) => {
        setViewBands(value);
    });

    // centers gaussian color toggle
    let centersUseGaussianColor = false;
    events.function('view.centersUseGaussianColor', () => centersUseGaussianColor);
    events.on('view.setCentersUseGaussianColor', (value: boolean) => {
        centersUseGaussianColor = value;
        events.fire('view.centersUseGaussianColor', value);
    });

    events.function('camera.getPose', () => {
        const camera = scene.camera;
        const position = camera.position;
        const focalPoint = camera.focalPoint;
        return {
            position: { x: position.x, y: position.y, z: position.z },
            target: { x: focalPoint.x, y: focalPoint.y, z: focalPoint.z }
        };
    });

    events.on('camera.setPose', (pose: { position: Vec3, target: Vec3 }, speed = 1) => {
        scene.camera.setPose(pose.position, pose.target, speed);
    });

    events.on('camera.focalPointPicked', (details: { position: Vec3 }) => {
        scene.pivotMarker?.show(details.position, scene.bound.halfExtents.length());
    });

    // measure settings

    let measureMode: 'distance' | 'polyline' | 'area' | 'angle' = 'distance';
    let measureUnits: 'm' | 'ft' = 'm';

    events.function('measure.mode', () => measureMode);
    events.function('measure.units', () => measureUnits);
    events.on('measure.setMode', (mode: 'distance' | 'polyline' | 'area' | 'angle') => {
        if ((mode === 'distance' || mode === 'polyline' || mode === 'area' || mode === 'angle') && mode !== measureMode) {
            measureMode = mode;
            events.fire('measure.mode', measureMode);
        }
    });

    events.on('measure.setUnits', (units: 'm' | 'ft') => {
        if ((units === 'm' || units === 'ft') && units !== measureUnits) {
            measureUnits = units;
            events.fire('measure.units', measureUnits);
        }
    });

    // measure picking settings

    let measureSplatPickMode: 'normal' | 'center' | 'none' = 'normal';
    let measureMinSplatOpacity = 0.5;

    events.function('measure.splatPickMode', () => measureSplatPickMode);
    events.function('measure.minSplatOpacity', () => measureMinSplatOpacity);

    events.on('measure.setSplatPickMode', (mode: 'normal' | 'center' | 'none') => {
        if (mode !== measureSplatPickMode) {
            measureSplatPickMode = mode;
            events.fire('measure.splatPickMode', measureSplatPickMode);
        }
    });

    events.on('measure.setMinSplatOpacity', (value: number) => {
        const next = Math.max(0, Math.min(1, value));
        if (next !== measureMinSplatOpacity) {
            measureMinSplatOpacity = next;
            events.fire('measure.minSplatOpacity', measureMinSplatOpacity);
        }
    });

    // hack: fire events to initialize UI
    events.fire('camera.fov', scene.camera.fov);
    events.fire('camera.tonemapping', scene.camera.tonemapping);
    events.fire('camera.mode', activeMode);
    events.fire('camera.controlMode', controlMode);
    events.fire('camera.overlay', cameraOverlay);
    events.fire('camera.splatSize', splatSize);
    events.fire('camera.flySpeed', scene.camera.flySpeed);
    events.fire('camera.walkSpeed', scene.camera.walkSpeed);
    events.fire('camera.collisionSources', collisionSources);
    events.fire('camera.collisionDistance', collisionDistance);
    events.fire('camera.collisionMinSplatOpacity', collisionMinSplatOpacity);
    events.fire('camera.walkHeight', scene.camera.walkHeight);
    events.fire('camera.walkSnapToMesh', scene.camera.walkSnapToMesh);
    events.fire('camera.boundsEnabled', cameraBoundsEnabled);
    events.fire('camera.boundsPadding', cameraBoundsPadding);
    events.fire('camera.bound', bound);
    events.fire('camera.showPoses', showPoses);
    events.fire('bookmarks.showMarkers', showBookmarkMarkers);
    events.fire('picture.enabledChanged', events.invoke('picture.enabled'));
    events.fire('clipBox.changed', clipBox);
    events.fire('clipBox.modeChanged', clipBoxMode);
    events.fire('view.outlineSelection', outlineSelection);
    events.fire('view.centersUseGaussianColor', centersUseGaussianColor);
    events.fire('view.bands', viewBands);
    events.fire('measure.mode', measureMode);
    events.fire('measure.units', measureUnits);
    events.fire('measure.splatPickMode', measureSplatPickMode);
    events.fire('measure.minSplatOpacity', measureMinSplatOpacity);
    events.fire('measure.historyChanged', events.invoke('measurements.list')?.map((item: any) => item.length ?? 0) ?? []);
    events.fire('case.metadataChanged', cloneCaseMetadata(caseMetadata));
    events.fire('units.mode', unitSystem.mode);
    events.fire('units.changed', unitSystem.mode);

    // doc serialization
    events.function('docSerialize.view', () => {
        const packC = (c: Color) => [c.r, c.g, c.b, c.a];
        const packV3 = (v: any) => [v?.x ?? 0, v?.y ?? 0, v?.z ?? 0];
        const packQ = (q: Quat | null | undefined) => [q?.x ?? 0, q?.y ?? 0, q?.z ?? 0, q?.w ?? 1];

        const clip = events.invoke('clipBox');
        return {
            bgColor: packC(events.invoke('bgClr')),
            selectedColor: packC(events.invoke('selectedClr')),
            unselectedColor: packC(events.invoke('unselectedClr')),
            lockedColor: packC(events.invoke('lockedClr')),
            shBands: events.invoke('view.bands'),
            centersSize: events.invoke('camera.splatSize'),
            outlineSelection: events.invoke('view.outlineSelection'),
            showGrid: events.invoke('grid.visible'),
            showBound: events.invoke('camera.bound'),
            showCameraPoses: events.invoke('camera.showPoses'),
            showBookmarkMarkers: events.invoke('bookmarks.showMarkers'),
            flySpeed: events.invoke('camera.flySpeed'),
            cameraBoundsEnabled: events.invoke('camera.boundsEnabled'),
            cameraBoundsPadding: events.invoke('camera.boundsPadding'),
            clipBox: clip ? {
                enabled: !!clip.enabled,
                center: packV3(clip.center),
                halfExtents: packV3(clip.halfExtents),
                rotation: packQ(clip.rotation)
            } : undefined,
            clipBoxMode: events.invoke('clipBox.mode') ?? 'shader',
            caseMetadata: cloneCaseMetadata(caseMetadata),
            annotations: events.invoke('annotations.list') ?? [],
            annotationLayers: events.invoke('annotations.layers') ?? [],
            bookmarks: events.invoke('bookmarks.list') ?? [],
            bookmarkSelection: events.functions.has('bookmarks.selected') ? (events.invoke('bookmarks.selected') ?? null) : null,
            tours: events.invoke('tours.list') ?? [],
            measurements: events.invoke('measurements.list') ?? [],
            measurementSelection: events.invoke('measurements.selected') ?? null,
            measurementSelectionNumber: events.invoke('measurements.selectedNumber') ?? null,
            measurementNextNumber: events.invoke('measurements.nextNumber') ?? 1,
            reconstructionTimeline: events.invoke('reconstructionTimeline.get') ?? undefined
        };
    });

    events.function('docDeserialize.view', (docView: any) => {
        events.fire('setBgClr', new Color(docView.bgColor));
        events.fire('setSelectedClr', new Color(docView.selectedColor));
        events.fire('setUnselectedClr', new Color(docView.unselectedColor));
        events.fire('setLockedClr', new Color(docView.lockedColor));
        events.fire('view.setBands', docView.shBands);
        events.fire('camera.setSplatSize', docView.centersSize);
        events.fire('view.setOutlineSelection', docView.outlineSelection);
        events.fire('grid.setVisible', docView.showGrid);
        events.fire('camera.setBound', docView.showBound);
        events.fire('camera.setShowPoses', docView.showCameraPoses ?? false);
        events.fire('camera.setFlySpeed', docView.flySpeed);

        // camera bounds
        if (typeof docView.cameraBoundsEnabled === 'boolean') {
            events.fire('camera.setBoundsEnabled', docView.cameraBoundsEnabled);
        }
        if (typeof docView.cameraBoundsPadding === 'number' && Number.isFinite(docView.cameraBoundsPadding)) {
            events.fire('camera.setBoundsPadding', docView.cameraBoundsPadding);
        }

        // global clip box (bounding box)
        if (docView?.clipBox && typeof docView.clipBox === 'object') {
            const cb = docView.clipBox;
            const center = Array.isArray(cb.center) && cb.center.length === 3 ? cb.center : null;
            const halfExtents = Array.isArray(cb.halfExtents) && cb.halfExtents.length === 3 ? cb.halfExtents : null;
            const rotation = Array.isArray(cb.rotation) && cb.rotation.length === 4 ? cb.rotation : null;
            events.fire('clipBox.set', {
                enabled: cb.enabled !== undefined ? !!cb.enabled : undefined,
                center: center ? new Vec3(center[0], center[1], center[2]) : undefined,
                halfExtents: halfExtents ? new Vec3(halfExtents[0], halfExtents[1], halfExtents[2]) : undefined,
                rotation: rotation ? new Quat(rotation[0], rotation[1], rotation[2], rotation[3]) : undefined
            });
        }
        if (docView?.clipBoxMode === 'shader' || docView?.clipBoxMode === 'test' || docView?.clipBoxMode === 'occluder') {
            events.fire('clipBox.setMode', docView.clipBoxMode);
        }

        caseMetadata = cloneCaseMetadata(docView.caseMetadata);
        events.fire('case.metadataChanged', cloneCaseMetadata(caseMetadata));

        events.fire('annotations.setAll', Array.isArray(docView.annotations) ? docView.annotations : []);
        events.fire('annotations.layers.setAll', Array.isArray(docView.annotationLayers) ? docView.annotationLayers : []);
        events.fire('bookmarks.setAll', Array.isArray(docView.bookmarks) ? docView.bookmarks : []);

        // restore selected bookmark (after list restore)
        if (events.functions.has('bookmarks.select')) {
            const sel = docView?.bookmarkSelection;
            if (typeof sel === 'string' && sel.length > 0) {
                events.fire('bookmarks.select', sel);
            } else {
                events.fire('bookmarks.select', null);
            }
        }
        events.fire('tours.setAll', Array.isArray(docView.tours) ? docView.tours : []);

        if (Array.isArray(docView.measurements)) {
            events.fire('measurements.restoreState', {
                items: docView.measurements,
                selected: docView.measurementSelection ?? docView.measurementSelectionNumber ?? null,
                nextNumber: docView.measurementNextNumber
            });
        } else if (docView.measurements && typeof docView.measurements === 'object') {
            // Legacy compatibility: earlier shape stored measurements under view.measurements.items.
            events.fire('measurements.restoreState', {
                items: Array.isArray(docView.measurements.items) ? docView.measurements.items : [],
                selected: docView.measurements.selected ?? null,
                nextNumber: docView.measurements.nextNumber
            });
        } else {
            events.fire('measurements.restoreState', {
                items: [],
                selected: null,
                nextNumber: 1
            });
        }

        events.fire('reconstructionTimeline.clear');
        if (docView.reconstructionTimeline) {
            events.fire('reconstructionTimeline.set', docView.reconstructionTimeline);
        }
    });
};

export { registerEditorEvents };
